import { useState, useEffect } from 'react';

// Global state to track loading status across all component instances
let isLoading = false;
let isLoaded = false;
const callbacks = [];

/**
 * Hook to lazy load Google Maps API only when needed
 *
 * Benefits:
 * - Saves 1.1MB on initial page load
 * - Only loads when map components are rendered
 * - Handles multiple map components loading simultaneously
 *
 * @returns {{ loaded: boolean, error: Error | null }}
 */
export function useGoogleMaps() {
  const [loaded, setLoaded] = useState(isLoaded);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Already loaded - return immediately
    if (isLoaded) {
      setLoaded(true);
      return;
    }

    // Currently loading - register callback
    if (isLoading) {
      callbacks.push({ setLoaded, setError });
      return;
    }

    // Start loading
    isLoading = true;

    // Check if already loaded by another script
    if (window.google && window.google.maps) {
      isLoaded = true;
      isLoading = false;
      setLoaded(true);
      return;
    }

    const script = document.createElement('script');
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      isLoaded = true;
      isLoading = false;
      setLoaded(true);

      // Notify all waiting components
      callbacks.forEach(cb => cb.setLoaded(true));
      callbacks.length = 0;
    };

    script.onerror = () => {
      isLoading = false;
      const err = new Error('Failed to load Google Maps API');
      setError(err);

      // Notify all waiting components of error
      callbacks.forEach(cb => cb.setError(err));
      callbacks.length = 0;
    };

    document.head.appendChild(script);

    // Cleanup function
    return () => {
      // Remove this component's callback if unmounted before loading completes
      const index = callbacks.findIndex(
        cb => cb.setLoaded === setLoaded && cb.setError === setError
      );
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    };
  }, []);

  return { loaded, error };
}
